import DOMPurify from "dompurify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";

import { Card } from "@components/cards/card";
import { InputClickToCopy } from "@components/forms/input-click-to-copy";
import {
  IRaffleDefaultRewardDTO,
  IRaffleDefaultStatusDTO,
  IRaffleDefaultTicketDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import {
  IRaffleCustomerBasicInfo,
  IRaffleTicketFilter,
  RaffleStatusTypes,
} from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { FaCircleInfo, FaGear, FaShareFromSquare } from "react-icons/fa6";
import { FaPlusCircle } from "react-icons/fa";
import { MyRaffleDefaultNumberTickets } from "@components/raffle-tickets/default/number";
import {
  FiEye,
  FiEyeOff,
  FiMinusCircle,
  FiPlusCircle,
  FiUser,
} from "react-icons/fi";
import { MyRaffleDefaultWordTickets } from "@components/raffle-tickets/default/word";
import { RaffleDefaultNumberRewards } from "@components/raffles/default/number/rewards";
import { ModalDefaultMessage } from "@components/modals/modal-messages";
import { ModalRaffleType } from "@components/modals/modal-raffle-type";
import { Pagination } from "@components/pagination";
import {
  IProfitTypeConfigTicketSellDTO,
  IProfitTypeDTO,
} from "@hooks/dtos/profit/IProfitTypeDTO";
import { RaffleDefaultCardCreateRewards } from "@components/raffle-creation/message/create/rewards";
import { RaffleStatusEnum } from "@hooks/enums/raffle";
import { RaffleDefaultCardCreatePayment } from "@components/raffle-creation/message/create/payment";
import { ModalInfo } from "@components/modals/modal-info";
import { goToHome, preciseSubtract } from "@hooks/functions/General";
import { IRaffleAffiliateDTO } from "@hooks/dtos/raffles/IRaffleAffiliateDTO";
import { ModalRaffleAffiliateCreate } from "@components/modals/modal-raffle-affiliate-create";
import { Button } from "@components/buttons/button";
import { ModalRaffleOwnerActions } from "@components/modals/modal-raffle-owner-actions";
import { RaffleOwnerResultInfo } from "@components/raffles/owner/resultInfo";
import { config } from "config";

interface IProfitInfoDTO {
  isOpen: boolean;
  header: string;
  title: string;
  text: string;
}

const masks = new Masks();
export function MyDefaultRaffleView() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");
  const campaign = searchParams.get("campaign");

  // --- Ticket Filters & Paginations --- //
  const [ticketPage, setTicketPage] = useState<number>(1);
  const [ticketStatus, setTicketStatus] = useState<IRaffleTicketFilter>("e");
  const [ticketTotalPage, setTicketTotalPage] = useState<number>(0);
  const [ticketOrder, setTicketOrder] = useState<string>("ASC");
  const [ticketQuantity, setTicketQuantity] = useState<number>(15);
  const ticketQuantityStep = 5;
  const ticketQuantityMin = 5;
  const ticketQuantityMax = 50;

  function changeTicketQuantity(nextQuantity: number) {
    const quantity = Math.min(
      ticketQuantityMax,
      Math.max(ticketQuantityMin, nextQuantity),
    );

    if (quantity === ticketQuantity) return;

    setTicketPage(1);
    setTicketQuantity(quantity);
  }

  // --- Financial Values --- //
  let [balanceTotal] = useState<number>(0);
  const [acquisitionValue, setAcquisitionValue] = useState<number>(0);
  const [inflowGrossTotal, setInflowGrossTotal] = useState<number>(0);
  const [commissionTotal, setCommissionTotal] = useState<number>(0);
  const [externalFeeTotal, setExternalFeeTotal] = useState<number>(0);
  const [internalFeeTotal, setInternalFeeTotal] = useState<number>(0);
  const [anotherFeeTotal, setAnotherFeeTotal] = useState<number>(0);
  const [productCostTotal, setProductCostTotal] = useState<number>(0);

  // --- Financial Profit Values (Ticket Sell) --- //
  const [profitType, setProfitType] = useState<IProfitTypeDTO>();
  const [profitTypeTicketSell, setProfitTypeTicketSell] =
    useState<IProfitTypeConfigTicketSellDTO>();

  // --- Raffle Default Infos --- //
  const [raffle, setRaffle] = useState<IRaffleCustomerBasicInfo>();
  const [rewards, setRewards] = useState<IRaffleDefaultRewardDTO[]>();
  const [tickets, setTickets] = useState<IRaffleDefaultTicketDTO[]>();
  const [raffleStatus, setRaffleStatus] = useState<IRaffleDefaultStatusDTO>({
    status: "x",
    text: "Carregando...",
    title: "Carregando...",
    color: "warning",
  });
  const [affiliateInfo, setAffiliateInfo] = useState<{
    total: number;
    highCommission: IRaffleAffiliateDTO;
  }>();

  const [shareLink, setShareLink] = useState<string>(``);
  const [soldProgress, setSoldProgress] = useState<number>(0);
  const [soldTicketAmount, setSoldTicketAmount] = useState<number>(0);
  const [availableTicketAmount, setAvailableTicketAmount] = useState<number>(0);

  const [show, setShow] = useState<{ [x: string]: boolean }>({
    description: false,
    financial: false,
    tickets: false,
    rewards: false,
  });

  // --- Control the Payment Modal to reload Page when Paid the Raffle --- //
  const [paymentModal, setPaymentModal] = useState<{
    paid: boolean;
    timeToClose: number;
  }>({
    paid: false,
    timeToClose: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState({
    affiliateCreate: false,
    messages: false,
    ownerActions: false,
  });
  const [profitInfo, setProfitInfo] = useState<IProfitInfoDTO>({
    isOpen: false,
    header: "",
    title: "",
    text: "",
  });
  const [modalMessageKey, setModalMessageKey] = useState<string | null>(null);
  const [modalRaffleTypeId, setModalRaffleTypeId] = useState<number | null>(
    null,
  );

  const [isLoading, setIsLoading] = useState<{ [x: string]: boolean }>({
    raffle: true,
    tickets: true,
    ticketProgressBar: true,
    campaignStatus: true,
    ticketStatus: true,
    partners: false,
    financialConfig: false,
    financialSummary: false,
    rewards: true,
  });

  const policy = window.trustedTypes?.createPolicy("sanitize-html", {
    createHTML: (input) => DOMPurify.sanitize(input),
  });

  async function loadCustomerOwnRaffle() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        raffle: true,
      }));

      const params = `?campaign=${campaign}&key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/summary${params}`,
      );

      setRaffle(response.data);

      setIsLoading((prev) => ({
        ...prev,
        raffle: false,
      }));
    } catch (error: any) {
      setTimeout(() => {
        goToHome();
      }, 1500);

      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadCustomerOwnRaffleTickets() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        tickets: true,
      }));

      const params = `?campaign=${campaign}&key=${key}&order=${ticketOrder}&page=${ticketPage}&quantity=${ticketQuantity}&status=${ticketStatus}`;
      const response = await api.get(
        `/customer/raffle/default/my/tickets${params}`,
      );

      setTickets(response.data.ticketData);

      const totalPages = Math.ceil(
        (response?.data?.quantity || 0) / ticketQuantity,
      );
      setTicketTotalPage(totalPages);

      setIsLoading((prev) => ({
        ...prev,
        tickets: false,
      }));

      loadTicketFilter();
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações dos Tickets.",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadCustomerOwnRaffleFinancialConfig() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        financialConfig: true,
      }));

      const params = `?campaign=${campaign}&key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/financialConfig${params}`,
      );

      setProfitType(response?.data?.profitTypeData);

      setProfitTypeTicketSell(response?.data?.profitConfig?.ticketSell);

      setIsLoading((prev) => ({
        ...prev,
        financialConfig: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações financeiras.",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadCustomerOwnRaffleTicketStatus() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        ticketStatus: true,
      }));

      const params = `?campaign=${campaign}&key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/tickets/status${params}`,
      );

      const _soldTicketAmount: number = response.data.sold || 0;
      setSoldTicketAmount(_soldTicketAmount);

      const totalTickets: number = raffle?.quantity || 0;
      const progress =
        Math.floor((_soldTicketAmount / totalTickets) * 100) || 0;
      setAvailableTicketAmount(Number(totalTickets - _soldTicketAmount) || 0);
      setSoldProgress(progress);

      setIsLoading((prev) => ({
        ...prev,
        ticketProgressBar: false,
        ticketStatus: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações dos Tickets vendidos.",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadCustomerOwnRaffleRewards() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        rewards: true,
      }));

      const params = `?campaign=${campaign}&key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/rewards${params}`,
      );

      const _productCostTotal: number = response.data.reduce(
        (acc: number, reward: IRaffleDefaultRewardDTO) => {
          return acc + (Number(reward.productCost) || 0);
        },
        0,
      );

      setRewards(response.data);

      setIsLoading((prev) => ({
        ...prev,
        rewards: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar as informações do(s) prêmio(s).",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadRaffleAffiliateInfo() {
    try {
      const params = `?key=${key}`;
      const response = await api.get(`/raffle/affiliate/amount${params}`);

      setAffiliateInfo(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar as informações do(s) afiliado(s).",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function loadCustomerOwnRaffleFinancialSummary() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        financialSummary: true,
      }));

      const params = `?campaign=${campaign}&key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/financialSummary${params}`,
      );

      const responseData = response.data;
      if (soldTicketAmount === 0) {
        setSoldTicketAmount(responseData.ticketSoldAmount);
      }
      setAcquisitionValue(responseData.acquisitionValue);
      setInflowGrossTotal(responseData.inflowGrossTotal);
      setCommissionTotal(-responseData.commissionTotal);
      setExternalFeeTotal(-responseData.externalFeeTotal);
      setInternalFeeTotal(-responseData.internalFeeTotal);
      setAnotherFeeTotal(-responseData.anotherFeeTotal);
      setProductCostTotal(-responseData.productCostTotal);

      setIsLoading((prev) => ({
        ...prev,
        financialSummary: false,
      }));
    } catch (error: any) {
      setTimeout(() => {
        goToHome();
      }, 1500);

      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar as informações financeiras da Campanha.",
        type: "error",
        theme: "dark",
      });
    }
  }

  const loadTicketFilter = () => {
    const Component =
      raffle?.type === "default_word"
        ? MyRaffleDefaultWordTickets
        : MyRaffleDefaultNumberTickets;

    return (
      <Component
        tickets={tickets || []}
        filter={ticketStatus}
        ticketsForRow={5}
        atSign={undefined}
      />
    );
  };

  const handleModalOpen = (messageKey: string) => {
    setModalMessageKey(messageKey);
    setIsModalOpen((prev) => ({
      ...prev,
      messages: true,
    }));
  };

  const handleModalRaffleTypeOpen = (typeId: number) => {
    setModalRaffleTypeId(typeId);
    setIsModalOpen((prev) => ({
      ...prev,
      affiliateCreate: true,
    }));
  };

  const handleModalClose = () => {
    setIsModalOpen((prev) => ({
      ...prev,
      messages: false,
      affiliateCreate: false,
    }));
  };

  useEffect(() => {
    loadCustomerOwnRaffle();
    loadRaffleAffiliateInfo();
  }, []);

  useEffect(() => {
    if (show.tickets && !tickets) {
      loadCustomerOwnRaffleTickets();
    }
  }, [show.tickets]);

  useEffect(() => {
    loadCustomerOwnRaffleTicketStatus();

    setRaffleStatus(getRaffleStatus(raffle?.status as RaffleStatusTypes));

    setIsLoading((prev) => ({
      ...prev,
      campaignStatus: false,
    }));

    const shareUrl = `${config.urls.store}/campaign/default?campaign=${campaign}&key=${key}`;
    setShareLink(shareUrl);
  }, [raffle]);

  useEffect(() => {
    loadCustomerOwnRaffleRewards();
  }, [show.rewards]);

  useEffect(() => {
    if (show.financial && !profitType) {
      loadCustomerOwnRaffleFinancialConfig();

      loadCustomerOwnRaffleFinancialSummary();
    }
  }, [show.financial]);

  useEffect(() => {
    setIsLoading((prev) => ({
      ...prev,
      tickets: true,
    }));

    loadCustomerOwnRaffleTickets();
  }, [ticketPage, ticketOrder, ticketQuantity, ticketStatus]);

  useEffect(() => {
    if (paymentModal.paid) {
      setTimeout(() => {
        window.location.reload();
      }, paymentModal.timeToClose);
    }
  }, [paymentModal]);

  useEffect(() => {
    if (profitType) {
      setProfitInfo((prev) => ({
        ...prev,
        header: "Tipo de Compromisso da Campanha",
        title: `${profitType.name}: ${profitType.description_short}`,
        text: profitType.description,
      }));
    }
  }, [profitType]);

  const inMaintenance = () => {
    pushToastify({
      message: `Em construção.`,
      type: "info",
      theme: "dark",
    });
  };

  const errorMessage = (message: string) => {
    pushToastify({
      message,
      type: "info",
      theme: "dark",
    });
  };

  return (
    <>
      {/* Main Things */}
      <Card>
        <span className="end-0 bg-info text-white p-2 rounded-top rounded-bottom-0 text-center">
          <span className="px-2">Campanha Própria</span>
        </span>
        <div className="card-header">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div className="text-center text-md-start">
              <div className="card-title">Informativo da Campanha</div>
            </div>
            <Button
              className="btn btn-primary"
              onClick={() =>
                setIsModalOpen((prev) => ({
                  ...prev,
                  ownerActions: true,
                }))
              }
            >
              <FaGear className="me-2" />
              Agendar Sorteio
            </Button>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-3 text-center">
            <div className="h5 fw-bold">{raffle?.name || `Carregando...`}</div>
            <div className="h6">
              {raffle?.description_short || `Carregando...`}
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12">
              <span className="small text-uppercase fw-bold opacity-75">
                Descrição
              </span>
              <div className="border rounded-4 overflow-hidden mt-2">
                <button
                  type="button"
                  className={`btn w-100 d-flex justify-content-between align-items-center gap-3 p-3 text-start ${
                    show.description ? "border-bottom" : ""
                  }`}
                  onClick={() =>
                    setShow((prev) => ({
                      ...prev,
                      description: !prev.description,
                    }))
                  }
                  aria-expanded={show.description}
                >
                  <span className="d-flex align-items-center gap-2">
                    <FiEye className="text-muted" />
                    <span>Descrição Completa</span>
                  </span>
                  <span className="btn btn-sm btn-light border rounded-pill px-3">
                    {show.description ? (
                      <FiEyeOff className="me-1" />
                    ) : (
                      <FiEye className="me-1" />
                    )}
                    {show.description ? "Ocultar" : "Mostrar"}
                  </span>
                </button>
                {show.description ? (
                  <div
                    className="px-3 py-3 overflow-auto"
                    style={{ maxHeight: 280 }}
                    dangerouslySetInnerHTML={{
                      __html:
                        policy?.createHTML(
                          raffle?.description || "Sem descrição",
                        ) || "Sem descrição",
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {raffle?.categories?.length ? (
            <div className="row">
              <div className="col-12">
                <span className="small text-uppercase fw-bold opacity-75">
                  Categorias
                </span>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {raffle.categories.map((raffleCategory) => {
                    const category = raffleCategory.categoryData;
                    if (!category) return null;

                    return (
                      <span
                        key={raffleCategory.id}
                        className="d-inline-flex align-items-center bg-white text-dark border rounded px-3 py-2 small fw-semibold"
                        title={category.description_short || category.name}
                      >
                        {category.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      {!isLoading.raffle && raffle && (
        <>
          {(() => {
            switch (raffle.status) {
              case RaffleStatusEnum.CREATING:
                return (
                  <RaffleDefaultCardCreateRewards
                    raffleId={key}
                    rewards={rewards}
                  />
                );

              case RaffleStatusEnum.PENDING:
                return (
                  <RaffleDefaultCardCreatePayment
                    raffleId={key}
                    onPaid={(paid, timeToClose) => {
                      setPaymentModal({ paid, timeToClose });
                    }}
                  />
                );

              default:
                return null;
            }
          })()}
        </>
      )}

      {/* Campaign Summary */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Resumo da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          {/* Type & Status */}
          <div className="row">
            <div className="d-flex justify-content-between">
              {/* Campaign Type */}
              <div
                className="col-md-5 mx-auto py-2 text-center border rounded-4 fw-bold"
                style={{ cursor: "pointer" }}
                title={
                  raffle?.typeData?.description_short ||
                  "Erro ao carregar informação."
                }
                onClick={() => {
                  if (raffle?.typeData?.id) {
                    handleModalRaffleTypeOpen(raffle?.typeData?.id);
                  } else {
                    errorMessage(
                      `Houve um erro ao carregar o tipo de Campanha.`,
                    );
                  }
                }}
              >
                <div className="d-flex justify-content-center align-items-center">
                  <span>{raffle?.typeData?.name}</span>
                </div>
              </div>

              {/* Campaign Status */}
              {isLoading.campaignStatus ? (
                <>
                  <div className="col-md-5 mx-auto p-2 text-center border rounded-4">
                    <span>Carregando status da campanha...</span>
                    <Skeleton count={1} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="col-md-5 mx-auto p-2 text-center border rounded-4"
                    title={raffleStatus?.title}
                  >
                    <div className="d-flex justify-content-center align-items-center">
                      <span
                        className={`rounded-circle d-inline-block me-2 bg-${raffleStatus?.color}`}
                        style={{ width: 15, height: 15 }}
                        title={raffleStatus?.title}
                      ></span>
                      <span>{raffleStatus?.text}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tickets & Partners Summary */}
          <div className="row mt-4">
            <div className="d-flex justify-content-between">
              {/* Tickets */}
              <div className="col-md-5 mx-auto border rounded-4">
                <div className="card-header text-center">
                  <div className="fs-5">
                    <div className="">Resumo de Tickets</div>
                  </div>
                  <div className="text-muted fs-6 p-0 m-0">
                    <small>Tickets da Campanha</small>
                  </div>
                </div>
                <div className="card-body border-bottom">
                  <div className="row d-flex justify-content-between text-center">
                    {isLoading.ticketStatus ? (
                      <>
                        <span>Carregando informações dos tickets...</span>
                        <Skeleton count={1} />
                      </>
                    ) : (
                      <>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(raffle?.quantity)}
                          </span>
                          <br />
                          <span>Total</span>
                        </div>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(soldTicketAmount)}
                          </span>
                          <br />
                          <span>Vendido{soldTicketAmount > 1 ? `s` : ``}</span>
                        </div>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(availableTicketAmount)}
                          </span>
                          <br />
                          <span>
                            Disponíve
                            {availableTicketAmount > 1 ? `is` : `l`}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Button */}
                  <div className="row col-6 mx-auto mt-4">
                    <Button
                      className="btn btn-info rounded text-white"
                      to={`/raffle/default/tickets?key=${key}`}
                    >
                      Detalhes
                    </Button>
                  </div>
                </div>
              </div>

              {/* Partners */}
              <div className="col-md-5 mx-auto border rounded-4">
                <div className="card-header text-center">
                  <div className="fs-5">
                    <div className="">Participações</div>
                  </div>
                  <div className="text-muted fs-6 p-0 m-0">
                    <small>Parceiros | Afiliações</small>
                  </div>
                </div>
                <div className="card-body border-bottom">
                  <div className="row d-flex justify-content-between text-center">
                    {isLoading.partners ? (
                      <>
                        <span>Carregando informações de parceiros...</span>
                        <Skeleton count={1} />
                      </>
                    ) : (
                      <>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {affiliateInfo?.total}
                          </span>
                          <br />
                          <span>Afiliados</span>
                        </div>
                        <div className="col-md-12 col-xxl-4">
                          <span className="fs-4 fw-bold">
                            {affiliateInfo?.highCommission?.percent || 0}%
                          </span>
                          <br />
                          <span>Comissão Máxima</span>
                        </div>
                        <div
                          className="col-md-12 col-xxl-4"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setIsModalOpen((prev) => ({
                              ...prev,
                              affiliateCreate: true,
                            }));
                          }}
                        >
                          <span className="fs-4 fw-bold">
                            <FaPlusCircle className="fs-4 text-success" />
                          </span>
                          <br />
                          <span>Afiliado</span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Button */}
                  <div className="row col-6 mx-auto mt-4">
                    <Button
                      className="btn btn-info rounded text-white"
                      to={`/raffle/affiliates?key=${key}`}
                    >
                      Detalhes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="row my-4">
            <div className="col-md-11 mx-auto text-center">
              {isLoading.ticketProgressBar ? (
                <>
                  <span>Carregando progresso da Campanha...</span>
                  <Skeleton count={1} />
                </>
              ) : (
                <>
                  <div className="d-flex flex-column gap-2">
                    <span className="fs-6 fw-bold">
                      Parabéns, {soldProgress}% dos tickets já foram vendidos.
                    </span>
                    <ProgressBar
                      now={soldProgress}
                      label={`${soldProgress}%`}
                      variant="primary"
                      animated
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <hr />

          {/* Share this Campaign */}
          <div className="row">
            <div className="col-10 mx-auto">
              <div className="text-center mb-3">
                <FaShareFromSquare className="fs-5 me-1" />
                <InputClickToCopy
                  label="Link de Compartilhamento"
                  value={shareLink}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {raffle?.status === RaffleStatusEnum.FINISHED && key && (
        <RaffleOwnerResultInfo raffleKey={key} />
      )}

      {/* Financial */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Financeiro da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-1">
            <div className="col-md-12">
              {/* Hide | Show Button */}
              <div className="col-md-2 mx-auto border rounded-4 py-1 mb-2 text-center">
                <span
                  className="fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setShow((prev) => ({
                      ...prev,
                      financial: !show.financial,
                    }))
                  }
                >
                  {show.financial ? `Ocultar` : `Mostrar`} Financeiro
                </span>
              </div>

              <div className={`${show.financial ? `` : `d-none`}`}>
                {/* Campaign Profit Info */}
                <div className="row">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Acordo de Campanha</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Tipo de compromisso com a Plataforma.</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.financialConfig ? (
                      <>
                        <Skeleton count={3} />
                      </>
                    ) : (
                      <>
                        <div className="row">
                          {/* Campaign Profit Type */}
                          <div className="d-flex justify-content-center mb-4">
                            <div className="col-md-11 mx-auto border rounded-4">
                              <div className="text-center py-2">
                                <div
                                  className="fs-5"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setProfitInfo((prev) => ({
                                      ...prev,
                                      isOpen: true,
                                    }));
                                  }}
                                >
                                  <span className="fw-normal me-1">
                                    {profitType?.name}
                                  </span>
                                  <span title={profitType?.description}>
                                    <FaCircleInfo />
                                  </span>
                                </div>
                                <div className="text-muted fs-6 p-0 m-0">
                                  <small>Tipo de Compromisso</small>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Fees, Internal & External Values */}
                          <div className="d-flex justify-content-between">
                            {/* Internal Fee Values */}
                            <div className="col-md-5 mx-auto border rounded-4">
                              <div className="card-header text-center">
                                <div className="fs-5">
                                  <div className="">Taxas da Plataforma</div>
                                </div>
                                <div className="text-muted fs-6 p-0 m-0">
                                  <small>
                                    Taxas, Tributos e Repasses Internos
                                  </small>
                                </div>
                              </div>
                              <div className="card-body border-bottom">
                                <div className="row d-flex justify-content-around text-center">
                                  <div className="col-md-12 col-xxl-4">
                                    <span className="fs-4 fw-bold">
                                      {masks.brlCurrency(
                                        profitTypeTicketSell?.partnerFlatFees,
                                      )}
                                    </span>
                                    <br />
                                    <span>Fixa</span>
                                  </div>
                                  <div className="col-md-12 col-xxl-4">
                                    <span className="fs-4 fw-bold">
                                      {masks.formatMilhar(
                                        profitTypeTicketSell?.partnerPercentFees,
                                      )}{" "}
                                      %
                                    </span>
                                    <br />
                                    <span>Percentual</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-center p-1">
                                <small>Encargos por cada ticket vendido.</small>
                              </div>
                            </div>

                            {/* External Fee Values */}
                            <div className="col-md-5 mx-auto border rounded-4">
                              <div className="card-header text-center">
                                <div className="fs-5">
                                  <div className="">Taxas Externas</div>
                                </div>
                                <div className="text-muted fs-6 p-0 m-0">
                                  <small>
                                    Integrações, Tarifas Bancárias, Impostos,
                                    etc...
                                  </small>
                                </div>
                              </div>
                              <div className="card-body border-bottom">
                                <div className="row d-flex justify-content-between text-center">
                                  <div className="col-md-12 col-xxl-3">
                                    <span className="fs-4 fw-bold">
                                      {masks.brlCurrency(
                                        profitTypeTicketSell?.externalFlatFees,
                                      )}
                                    </span>
                                    <br />
                                    <span>Gateway</span>
                                  </div>
                                  <div className="col-md-12 col-xxl-3">
                                    <span className="fs-4 fw-bold">
                                      {masks.formatMilhar(
                                        profitTypeTicketSell?.externalPercentFees,
                                      )}{" "}
                                      %
                                    </span>
                                    <br />
                                    <span>Gateway</span>
                                  </div>
                                  <div className="col-md-12 col-xxl-3">
                                    <span className="fs-4 fw-bold">
                                      {masks.brlCurrency(
                                        profitTypeTicketSell?.anotherFlatFees,
                                      )}
                                    </span>
                                    <br />
                                    <span>Outras</span>
                                  </div>
                                  <div className="col-md-12 col-xxl-3">
                                    <span className="fs-4 fw-bold">
                                      {masks.formatMilhar(
                                        profitTypeTicketSell?.anotherPercentFees,
                                      )}{" "}
                                      %
                                    </span>
                                    <br />
                                    <span>Outras</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-center p-1">
                                <small>
                                  Tributos e taxas por cada ticket vendido.
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <hr />

                {/* Extract */}
                <div className="card-body pt-0">
                  {isLoading.financialSummary ? (
                    <>
                      <Skeleton count={7} height={20} />
                    </>
                  ) : (
                    <>
                      {/* Some Extract Extra Info */}
                      <div className="row">
                        <div className="pb-2 text-center">
                          <div className="fs-5">
                            <div className="">Resumo dos Tickets</div>
                          </div>
                          <div className="text-muted fs-6 p-0 m-0">
                            <small>
                              Informações como quantidade, estimativas e
                              entradas totais.
                            </small>
                          </div>
                        </div>

                        <div className="d-flex justify-content-between pb-4">
                          {/* Ticket Sold Status */}
                          <div className="col-md-6 mx-auto border rounded-4">
                            <div className="card-header text-center">
                              <div className="fs-5">
                                <div className="">Informações dos Tickets</div>
                              </div>
                              <div className="text-muted fs-6 p-0 m-0">
                                <small>Informações e Estimativas</small>
                              </div>
                            </div>
                            <div className="card-body border-bottom">
                              <div className="row d-flex justify-content-between text-center">
                                {/* Ticket Amount | Quantity */}
                                <div className="col-md-12 col-xxl-3">
                                  <span className="fs-4 fw-bold">
                                    {masks.formatMilhar(soldTicketAmount)}
                                  </span>
                                  <br />
                                  <span>
                                    Vendido{soldTicketAmount > 0 ? `s` : ``}
                                  </span>
                                </div>
                                {/* Price Un. */}
                                <div className="col-md-12 col-xxl-3">
                                  <span className="fs-4 fw-bold">
                                    {masks.brlCurrency(raffle?.price)}
                                  </span>
                                  <br />
                                  <span>Preço Un.</span>
                                </div>
                                {/* Inflow */}
                                <div className="col-md-12 col-xxl-3">
                                  <span className="fs-4 fw-bold">
                                    {masks.brlCurrency(inflowGrossTotal)}
                                  </span>
                                  <br />
                                  <span>Entradas</span>
                                </div>
                                {/* Price Total (price * ticket amount) */}
                                <div className="col-md-12 col-xxl-3">
                                  <span className="fs-4 fw-bold">
                                    {masks.brlCurrency(
                                      (raffle?.quantity || 0) *
                                        (raffle?.price || 0),
                                    )}
                                  </span>
                                  <br />
                                  <span>Arrecação Estimada</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-center p-1">
                              <small>Resumo financeiro dos tickets.</small>
                            </div>
                          </div>
                        </div>

                        <hr />

                        {/* Extract Table */}
                        <div className="pb-2 text-center">
                          <div className="fs-5">
                            <div className="">Extrato da Campanha</div>
                          </div>
                          <div className="text-muted fs-6 p-0 m-0">
                            <small>
                              Resumo de entradas, comissões, taxas, impostos e
                              lucro.
                            </small>
                          </div>
                        </div>
                        <table className="table table-hover border caption-top">
                          <thead>
                            <tr>
                              <th scope="col">Objeto</th>
                              <th scope="col" className="text-end">
                                Valor
                              </th>
                              <th scope="col" className="text-end">
                                Saldo
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Inflows */}
                            {(() => {
                              balanceTotal = inflowGrossTotal;
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleModalOpen("Inflow")}
                                >
                                  <th scope="row">
                                    <span className="">Entradas </span>
                                  </th>
                                  <td className="fs-5 text-success text-end">
                                    {masks.brlCurrency(inflowGrossTotal)}
                                  </td>
                                  <td className="fs-5 text-success text-end">
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Comissions */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(commissionTotal),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleModalOpen("Commission")}
                                >
                                  <th scope="row">
                                    <span className="">Comissões</span>
                                  </th>
                                  <td className="fs-5 text-warning text-end">
                                    {masks.brlCurrency(commissionTotal)}
                                  </td>
                                  <td className="fs-5 text-success text-end">
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* External Fees */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(externalFeeTotal),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleModalOpen("feeExternal")}
                                >
                                  <th scope="row">
                                    <span className="">Taxas Externas</span>
                                  </th>

                                  <td className="fs-5 text-warning text-end">
                                    {masks.brlCurrency(externalFeeTotal)}
                                  </td>
                                  <td className="fs-5 text-success text-end">
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Acquisition Price */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(acquisitionValue),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    handleModalOpen("campaignAcquisition")
                                  }
                                >
                                  <th scope="row">
                                    <span className="">
                                      Aquisição da Campanha
                                    </span>
                                  </th>
                                  <td className="fs-5 text-warning text-end">
                                    {masks.brlCurrency(acquisitionValue)}
                                  </td>
                                  <td
                                    className={`fs-5 text-${
                                      balanceTotal > 0 ? `success` : `warning`
                                    } text-end`}
                                  >
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Internal Fees */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(internalFeeTotal),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleModalOpen("feeInternal")}
                                >
                                  <th scope="row">
                                    <span className="">
                                      Taxas da Plataforma
                                    </span>
                                  </th>
                                  <td className="fs-5 text-warning text-end">
                                    {masks.brlCurrency(internalFeeTotal)}
                                  </td>
                                  <td
                                    className={`fs-5 text-${
                                      balanceTotal > 0 ? `success` : `warning`
                                    } text-end`}
                                  >
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Another Fees */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(anotherFeeTotal),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleModalOpen("feeAnother")}
                                >
                                  <th scope="row">
                                    <span className="">
                                      Outras Taxas / Impostos
                                    </span>
                                  </th>
                                  <td
                                    className={`fs-5 text-${
                                      anotherFeeTotal < 0
                                        ? `warning`
                                        : `primary`
                                    } text-end`}
                                  >
                                    {masks.brlCurrency(anotherFeeTotal)}
                                  </td>
                                  <td
                                    className={`fs-5 text-${
                                      balanceTotal > 0 ? `success` : `warning`
                                    } text-end`}
                                  >
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Product(s) Cost(s) */}
                            {(() => {
                              balanceTotal = preciseSubtract(
                                balanceTotal,
                                Math.abs(productCostTotal),
                              );
                              return (
                                <tr
                                  style={{ cursor: "pointer" }}
                                  onClick={() => 0}
                                >
                                  <th scope="row">
                                    <span className="">
                                      Custos do(s) Produto(s)
                                    </span>
                                  </th>
                                  <td className="fs-5 text-warning text-end">
                                    {masks.brlCurrency(-productCostTotal)}
                                  </td>
                                  <td
                                    className={`fs-5 text-${
                                      balanceTotal > 0 ? `success` : `warning`
                                    } text-end`}
                                  >
                                    {masks.brlCurrency(balanceTotal)}
                                  </td>
                                </tr>
                              );
                            })()}
                            {/* Campaign Profit */}
                            <tr
                              style={{ cursor: "pointer" }}
                              onClick={() => handleModalOpen("Profit")}
                            >
                              <th
                                scope="row"
                                colSpan={2}
                                className="text-start"
                              >
                                Saldo Líquido
                              </th>
                              <td
                                colSpan={2}
                                className={`fs-5 text-${
                                  balanceTotal > 0 ? `success` : `warning`
                                } text-end`}
                              >
                                {masks.brlCurrency(balanceTotal)}
                              </td>
                            </tr>
                          </tbody>
                          <tfoot></tfoot>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tickets */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Tickets da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-1">
            <div className="col-md-12 text-center">
              <div className="col-md-2 mx-auto border rounded-4 py-1 mb-2 text-center">
                <span
                  className="fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setShow((prev) => ({
                      ...prev,
                      tickets: !show.tickets,
                    }))
                  }
                >
                  {show.tickets ? `Ocultar` : `Mostrar`} Tickets
                </span>
              </div>
              <div className={`${show.tickets ? `` : `d-none`} mt-4`}>
                {!tickets ? (
                  <>
                    <Skeleton count={3} />
                  </>
                ) : (
                  <>
                    {/* Filters */}
                    <div className="row justify-content-around mb-3">
                      <div className="col-md-2">
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-group flex-grow-1">
                            <select
                              className="form-select"
                              aria-label="Filtro de usuário"
                              value={0}
                              onChange={() => undefined}
                            >
                              <option value="0">Selecione...</option>
                              <option value="1">(Em breve)</option>
                            </select>
                          </div>
                          <FiUser
                            className="fs-4 flex-shrink-0"
                            title="Filtrar por usuário específico (em breve)."
                          />
                        </div>
                      </div>

                      <div className="col-md-8">
                        <div
                          className="d-flex justify-content-center align-items-center gap-3"
                          title="Quantidade de tickets exibidos por página."
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() =>
                              changeTicketQuantity(
                                ticketQuantity - ticketQuantityStep,
                              )
                            }
                            disabled={ticketQuantity <= ticketQuantityMin}
                            aria-label="Exibir menos tickets por página"
                            title="Exibir menos tickets"
                          >
                            <FiMinusCircle className="fs-4" />
                          </button>
                          <output
                            className="bg-light border rounded px-3 py-2 text-center text-muted"
                            style={{ minWidth: 72 }}
                            aria-label={`${ticketQuantity} tickets por página`}
                          >
                            {ticketQuantity}
                          </output>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() =>
                              changeTicketQuantity(
                                ticketQuantity + ticketQuantityStep,
                              )
                            }
                            disabled={ticketQuantity >= ticketQuantityMax}
                            aria-label="Exibir mais tickets por página"
                            title="Exibir mais tickets"
                          >
                            <FiPlusCircle className="fs-4" />
                          </button>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="d-flex justify-content-md-end align-items-center gap-2">
                          <FiEye
                            className="fs-5 flex-shrink-0"
                            title="Filtro de tickets exibidos."
                          />
                          <div
                            className="form-group flex-grow-1"
                            style={{ maxWidth: 350 }}
                          >
                            <select
                              className="form-select"
                              aria-label="Filtro de tickets"
                              value={ticketStatus}
                              onChange={(event) => {
                                setTicketPage(1);
                                setTicketStatus(
                                  event.target.value as IRaffleTicketFilter,
                                );
                              }}
                            >
                              <option value="e">Todos</option>
                              <option value="a">Disponíveis</option>
                              <option value="u">Indisponíveis</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Pagination */}
                    <div className="row d-flex flex">
                      {/* Ticket Component: Load Options */}
                      {tickets && tickets.length > 0 ? (
                        <>
                          {loadTicketFilter()}
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="">
                              <span>
                                Página {ticketPage} de {ticketTotalPage} página
                                {ticketTotalPage > 1 ? `s` : ``}.
                              </span>
                            </div>
                            <div className="">
                              <Pagination
                                page={ticketPage}
                                setPage={setTicketPage}
                                totalPages={ticketTotalPage}
                                maxPageShow={5}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="card-body">
                            <div className="d-flex flex-column gap-2">
                              <span>Nenhum ticket foi encontrado</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Rewards */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">
              Prêmio{rewards && rewards?.length > 1 ? `s` : ``} da Campanha
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-1">
            <div className="col-md-12 text-center">
              <div className="col-md-2 mx-auto border rounded-4 py-1 mb-2">
                <span
                  className="fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setShow((prev) => ({
                      ...prev,
                      rewards: !show.rewards,
                    }))
                  }
                >
                  {show.rewards ? `Ocultar` : `Mostrar`} Premiaç
                  {rewards && rewards?.length > 1 ? `ões` : `ão`}
                </span>
              </div>
              <div className={`${show.rewards ? `` : `d-none`} mt-4`}>
                {isLoading.rewards ? (
                  <>
                    <Skeleton count={2} />
                  </>
                ) : (
                  <>{<RaffleDefaultNumberRewards rewards={rewards || []} />}</>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ModalInfo
        header={profitInfo.header}
        title={profitInfo.title}
        text={profitInfo.text}
        isOpen={profitInfo.isOpen || false}
        onClose={() => {
          setProfitInfo((prev) => ({
            ...prev,
            isOpen: false,
          }));
        }}
      />

      {key && isModalOpen.affiliateCreate && (
        <ModalRaffleAffiliateCreate
          raffleKey={key}
          isOpen={isModalOpen.affiliateCreate}
          onClose={(created) => {
            setIsModalOpen((prev) => ({
              ...prev,
              affiliateCreate: false,
            }));
            if (created) {
              loadRaffleAffiliateInfo();
            }
          }}
        />
      )}

      {isModalOpen.messages &&
        (() => {
          if (modalMessageKey) {
            return (
              <ModalDefaultMessage
                messageKey={modalMessageKey}
                onClose={handleModalClose}
              />
            );
          } else if (modalRaffleTypeId) {
            return (
              <ModalRaffleType
                typeId={modalRaffleTypeId}
                onClose={handleModalClose}
              />
            );
          } else {
            return handleModalClose;
          }
        })()}

      {isModalOpen.ownerActions && (
        <ModalRaffleOwnerActions
          isOpen={isModalOpen.ownerActions}
          onClose={() =>
            setIsModalOpen((prev) => ({
              ...prev,
              ownerActions: false,
            }))
          }
          campaign={campaign}
          raffleKey={key}
          soldPercent={soldProgress}
          soldTickets={soldTicketAmount}
          totalTickets={raffle?.quantity}
        />
      )}
    </>
  );
}
